import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { getPurchaseOrderReportApi, exportPurchaseOrderReportApi, uniformErrorMessage } from 'api/services/uniformManagement';
import { itemRender, handlePageChange, defaultPagination, pageSizeOptions } from 'utils/table';
import { formatDateHandler } from 'utils/common';
import { getStatusBadge, poStatusOptions } from '../../constants';
import ExcelExportButton from 'components/ExcelExportButton';
import ReportFilterBar, { cleanParams } from '../ReportFilterBar';
import useStickyIdentity from '../../components/useStickyIdentity';
import { UNIFORM_LANE, truncatedText } from '../../components/columns';

const toastOpts = { position: 'top-right', autoClose: 3000, transition: Bounce };

const FIELDS = ['dateRange', 'status', 'item', 'size', 'search', 'supplier'];

/** Unlike the other three tabs, this one is usually opened with a specific PO
 *  in hand, so `search` (PO or invoice number) leads, followed by the period
 *  and the receiving status — Draft/Open/Partially Received is the question
 *  asked of a PO report when it is not asked of one PO. Item, size and
 *  supplier narrow a list you are already looking at. */
const PRIMARY_FIELDS = ['search', 'dateRange', 'status'];

/** Line-level PO receiving report — one row per PO line, not per PO. */
const PurchaseOrderReport = ({ itemOptions, itemOptionsLoading }) => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageSettings, setPageSettings] = useState({ ...defaultPagination });
  const [draft, setDraft] = useState({});
  const [applied, setApplied] = useState({});

  // Guarded with `ignore` so a slow response for filters the user has since
  // changed can't clobber newer results (including the loading-state reset).
  const fetchData = useCallback(() => {
    let ignore = false;
    setLoading(true);
    const params = { ...cleanParams(applied), page: pageSettings.page, page_size: pageSettings.pageSize };
    getPurchaseOrderReportApi(params).then((response) => {
      if (ignore) return;
      setData(response.data?.results || []);
      setPageSettings((prev) => ({ ...prev, total: response.data?.count || 0 }));
    }).catch((e) => {
      if (ignore) return;
      toast.error(uniformErrorMessage(e, 'Failed to load purchase order report'), toastOpts);
    }).finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [applied, pageSettings.page, pageSettings.pageSize]);

  useEffect(() => fetchData(), [fetchData]);

  const handleChange = (pagination, filters, sorter) => {
    setPageSettings(handlePageChange(pagination, filters, sorter, pageSettings));
  };

  const handleApply = () => {
    setApplied(draft);
    setPageSettings((prev) => ({ ...prev, page: 1 }));
  };

  const handleClear = () => {
    setDraft({});
    setApplied({});
    setPageSettings((prev) => ({ ...prev, page: 1 }));
  };

  const pinLeft = useStickyIdentity();

  const columns = [
    {
      // Pinned: twelve columns, and the PO number is the row's name.
      title: 'PO Number', dataIndex: 'po_number', ...pinLeft(160),
      render: (text, record) => (
        <button type="button" className="btn btn-link p-0" style={{ color: '#D3001A' }}
          onClick={() => navigate(`/uniform-management/purchase-orders/${record.purchase_order_id}`)}>
          {text}
        </button>
      )
    },
    { title: 'PO Date', dataIndex: 'po_date', render: (d) => formatDateHandler(d) },
    { title: 'Supplier', dataIndex: 'supplier_name', render: (v) => v || '-' },
    {
      title: 'Status', dataIndex: 'status',
      render: (status) => <span style={getStatusBadge(status)}>{status ? status.replace('_', ' ') : '-'}</span>
    },
    { title: 'Uniform', dataIndex: 'uniform_item_name', ...truncatedText(UNIFORM_LANE) },
    { title: 'Size', dataIndex: 'size_code', render: (v) => v || '-' },
    { title: 'Ordered', dataIndex: 'ordered_quantity' },
    { title: 'Received', dataIndex: 'received_quantity' },
    {
      title: 'Balance', dataIndex: 'balance_quantity',
      render: (val, record) => {
        const isOutstanding = val > 0 && record.status !== 'CANCELLED';
        return <span style={{ color: isOutstanding ? '#ff4d4f' : 'inherit', fontWeight: isOutstanding ? 600 : 400 }}>{val}</span>;
      }
    },
    { title: 'Last Receiving', dataIndex: 'last_received_date', render: (d) => d ? formatDateHandler(d) : '-' },
    { title: 'Receipts', dataIndex: 'receipt_references', render: (v) => v || '-' },
    { title: 'Invoice', dataIndex: 'invoice_number', render: (v) => v || '-' },
  ];

  return (
    <div>
      <div className="d-flex justify-content-end mb-2">
        <ExcelExportButton framed apiFn={exportPurchaseOrderReportApi} params={cleanParams(applied)} filename="Uniform PO Receiving.xlsx" />
      </div>
      <ReportFilterBar
        fields={FIELDS} primaryFields={PRIMARY_FIELDS}
        value={draft} appliedValue={applied} onChange={setDraft} statusOptions={poStatusOptions}
        itemOptions={itemOptions} itemOptionsLoading={itemOptionsLoading}
        searchPlaceholder="Search PO or invoice number..."
        onApply={handleApply} onClear={handleClear}
      />
      {/* No stable per-line id in the report row (only the parent purchase_order_id),
          so rowKey folds in the row index to stay unique within the page. */}
      <Table columns={columns} dataSource={data} rowKey={(r, i) => `${r.purchase_order_id}-${i}`} loading={loading}
        pagination={{
          total: pageSettings.total, pageSize: pageSettings.pageSize, current: pageSettings.page,
          pageSizeOptions, itemRender, showSizeChanger: true
        }}
        onChange={handleChange} scroll={{ x: 'max-content' }} />
    </div>
  );
};

export default PurchaseOrderReport;
